import { ICount } from 'backend2/interfaces/common';
import { checkIsCustomer, checkIsExecutor } from 'src/libs/backend2/parser';
import AccountingStore from 'src/stores/Accounting';
import CustomsStore from 'src/stores/Customs';
import OrdersStore from 'src/stores/Orders';
import UsersStore from 'src/stores/Users';
import WarehousesStore from 'src/stores/Warehouses';

const FETCH_PRESETS_ITERATION_LIMIT = 5000;
const FETCH_PRESETS_MAX_ITERATION = 10;
const FETCH_PRESETS_ITERATION_DELAY = 3000;

const NOT_FETCHING_COUNT: ICount = {
	count: BigInt(0),
};

export async function fetchPresets() {
	const currentUserType = UsersStore.viewerType;
	const isCurrentUserExecutor = checkIsExecutor(null, currentUserType);
	const isCurrentUserCustomer = checkIsCustomer(null, currentUserType);

	await OrdersStore.getOrderStatusesConfig();
	await AccountingStore.fetchAccountsScheme();

	const [
		promiseSettledNonArchivedUsersCount,
		promiseSettledArchivedUsersCount,
		promiseSettledWarehousesCount,
		promiseSettledOrderCustomKindsCount,
		promiseSettledOrderCustomCostsCount,
		promiseSettledOrderCustomsInsuranceFeeCount,
		promiseSettledOrderCustomsRKOCount,
		promiseSettledOrderCustomsPaymentToExecutorCount,
	] = await Promise.allSettled([
		UsersStore.fetchUsersCount({ isArchived: false }),
		UsersStore.fetchUsersCount({ isArchived: true }),
		WarehousesStore.fetchWarehousesCount({ isRemoved: 'all' }),
		CustomsStore.fetchOrderCustomKindsCount({ isRemoved: 'all' }),

		isCurrentUserCustomer || isCurrentUserExecutor ? Promise.resolve(NOT_FETCHING_COUNT) : CustomsStore.fetchOrdersCustomCostsCount({ isRemoved: 'all' }),
		isCurrentUserCustomer || isCurrentUserExecutor
			? Promise.resolve(NOT_FETCHING_COUNT)
			: CustomsStore.fetchOrdersCustomsInsuranceFeeCount({ isRemoved: 'all' }),
		isCurrentUserCustomer || isCurrentUserExecutor ? Promise.resolve(NOT_FETCHING_COUNT) : CustomsStore.fetchOrdersCustomsRKOCount({ isRemoved: 'all' }),
		isCurrentUserCustomer || isCurrentUserExecutor
			? Promise.resolve(NOT_FETCHING_COUNT)
			: CustomsStore.fetchOrdersCustomsPaymentToExecutorCount({ isRemoved: 'all' }),
	]);

	let fetchedNonArchivedUsersCount = 0n;
	let fetchedArchivedUsersCount = 0n;
	let fetchedWarehousesCount = 0n;
	let fetchedOrderCustomKindsCount = 0n;
	let fetchedOrderCustomCostsCount = 0n;
	let fetchedOrderCustomsInsuranceFeeCount = 0n;
	let fetchedOrderCustomsRKOCount = 0n;
	let fetchedOrderCustomsPaymentToExecutorCount = 0n;

	const nonArchivedUsersTotalCount = promiseSettledNonArchivedUsersCount.status === 'fulfilled' ? promiseSettledNonArchivedUsersCount.value.count : 1000n;
	const archivedUsersTotalCount = promiseSettledArchivedUsersCount.status === 'fulfilled' ? promiseSettledArchivedUsersCount.value.count : 1000n;
	const warehousesTotalCount = promiseSettledWarehousesCount.status === 'fulfilled' ? promiseSettledWarehousesCount.value.count : 1000n;
	const orderCustomKindsTotalCount = promiseSettledOrderCustomKindsCount.status === 'fulfilled' ? promiseSettledOrderCustomKindsCount.value.count : 1000n;
	const orderCustomCostsTotalCount = promiseSettledOrderCustomCostsCount.status === 'fulfilled' ? promiseSettledOrderCustomCostsCount.value.count : 1000n;
	const orderCustomsInsuranceFeeTotalCount =
		promiseSettledOrderCustomsInsuranceFeeCount.status === 'fulfilled' ? promiseSettledOrderCustomsInsuranceFeeCount.value.count : 1000n;
	const orderCustomsRKOCountTotalCount = promiseSettledOrderCustomsRKOCount.status === 'fulfilled' ? promiseSettledOrderCustomsRKOCount.value.count : 1000n;
	const orderCustomsPaymentToExecutorTotalCount =
		promiseSettledOrderCustomsPaymentToExecutorCount.status === 'fulfilled' ? promiseSettledOrderCustomsPaymentToExecutorCount.value.count : 1000n;

	for (let i = 0; i < FETCH_PRESETS_MAX_ITERATION; i++) {
		const offset = i * FETCH_PRESETS_ITERATION_LIMIT;

		try {
			const nonArchivedUsers =
				fetchedNonArchivedUsersCount < nonArchivedUsersTotalCount
					? await UsersStore.fetchUsers({ isArchived: false, limit: FETCH_PRESETS_ITERATION_LIMIT, offset })
					: [];
			fetchedNonArchivedUsersCount += nonArchivedUsers.length ? BigInt(nonArchivedUsers.length) : nonArchivedUsersTotalCount;
		} catch (_ex) {
			fetchedNonArchivedUsersCount += nonArchivedUsersTotalCount;
		}
		try {
			const archivedUsers =
				fetchedArchivedUsersCount < archivedUsersTotalCount
					? await UsersStore.fetchUsers({ isArchived: true, limit: FETCH_PRESETS_ITERATION_LIMIT, offset })
					: [];
			fetchedArchivedUsersCount += archivedUsers.length ? BigInt(archivedUsers.length) : archivedUsersTotalCount;
		} catch (_ex) {
			fetchedArchivedUsersCount += archivedUsersTotalCount;
		}

		try {
			const warehouses =
				fetchedWarehousesCount < warehousesTotalCount
					? await WarehousesStore.fetchWarehouses({ isRemoved: 'all', limit: FETCH_PRESETS_ITERATION_LIMIT, offset })
					: [];
			fetchedWarehousesCount += warehouses.length ? BigInt(warehouses.length) : warehousesTotalCount;
		} catch (_ex) {
			fetchedWarehousesCount += warehousesTotalCount;
		}

		try {
			const orderCustomKinds =
				fetchedOrderCustomKindsCount < orderCustomKindsTotalCount
					? await CustomsStore.fetchOrderCustomKinds({ isRemoved: 'all', limit: FETCH_PRESETS_ITERATION_LIMIT, offset })
					: [];
			fetchedOrderCustomKindsCount += orderCustomKinds.length ? BigInt(orderCustomKinds.length) : orderCustomKindsTotalCount;
		} catch (_ex) {
			fetchedOrderCustomKindsCount += orderCustomKindsTotalCount;
		}

		try {
			const customs =
				fetchedOrderCustomCostsCount < orderCustomCostsTotalCount
					? await CustomsStore.fetchOrdersCustomCosts({ isRemoved: 'all', limit: FETCH_PRESETS_ITERATION_LIMIT, offset })
					: [];
			fetchedOrderCustomCostsCount += customs.length ? BigInt(customs.length) : orderCustomCostsTotalCount;
		} catch (_ex) {
			fetchedOrderCustomCostsCount += orderCustomCostsTotalCount;
		}

		try {
			const customs =
				fetchedOrderCustomsInsuranceFeeCount < orderCustomsInsuranceFeeTotalCount
					? await CustomsStore.fetchOrdersCustomsInsuranceFee({ isRemoved: 'all', limit: FETCH_PRESETS_ITERATION_LIMIT, offset })
					: [];
			fetchedOrderCustomsInsuranceFeeCount += customs.length ? BigInt(customs.length) : orderCustomsInsuranceFeeTotalCount;
		} catch (_ex) {
			fetchedOrderCustomsInsuranceFeeCount += orderCustomsInsuranceFeeTotalCount;
		}

		try {
			const customs =
				fetchedOrderCustomsRKOCount < orderCustomsRKOCountTotalCount
					? await CustomsStore.fetchOrdersCustomsRKO({ isRemoved: 'all', limit: FETCH_PRESETS_ITERATION_LIMIT, offset })
					: [];
			fetchedOrderCustomsRKOCount += customs.length ? BigInt(customs.length) : orderCustomsRKOCountTotalCount;
		} catch (_ex) {
			fetchedOrderCustomsRKOCount += orderCustomsRKOCountTotalCount;
		}

		try {
			const customs =
				fetchedOrderCustomsPaymentToExecutorCount < orderCustomsPaymentToExecutorTotalCount
					? await CustomsStore.fetchOrdersCustomsPaymentToExecutor({ isRemoved: 'all', limit: FETCH_PRESETS_ITERATION_LIMIT, offset })
					: [];
			fetchedOrderCustomsPaymentToExecutorCount += customs.length ? BigInt(customs.length) : orderCustomsPaymentToExecutorTotalCount;
		} catch (_ex) {
			fetchedOrderCustomsPaymentToExecutorCount += orderCustomsPaymentToExecutorTotalCount;
		}

		await new Promise((resolve) => setTimeout(resolve, FETCH_PRESETS_ITERATION_DELAY));
	}
}
